import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { MinigridLocationEnriched } from '../../types/minigridtypes';
import minigridAPI from '../../service/minigridAPI';
import MicroModalSelectorSearch from '../../../../common/components/organisme/MicroModalSelectorSearch/MicroModalSelectorSearch';

export interface MinigridLocationSelectorProps {
  isError?: boolean;
  onChange: (locations: MinigridLocationEnriched[]) => void;
  onUserSelect?: (
    locations: MinigridLocationEnriched[],
    added: MinigridLocationEnriched | null,
  ) => void;
  value: MinigridLocationEnriched[];
  variant?: 'dark' | 'light';
  initLocationNames?: string[] | null;
  currentMinigridId?: string | null;
  label?: string;
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  margin: '0 6px 6px 0',
  borderRadius: 16,
  background: '#eef2ff',
  color: '#1e3a8a',
  fontSize: 12,
  border: '1px solid #c7d2fe',
};

const chipRemoveStyle: React.CSSProperties = {
  cursor: 'pointer',
  color: '#6b7280',
  fontWeight: 700,
  lineHeight: 1,
};

export default function MinigridLocationSelector({
  onChange,
  onUserSelect,
  isError,
  value,
  variant = 'light',
  initLocationNames,
  currentMinigridId,
  label = 'Localisation minigrid',
}: MinigridLocationSelectorProps) {
  const [valueQuery, setValueQuery] = useState('');
  const [initLoaded, setInitLoaded] = useState(false);

  const locationsQuery = useQuery({
    queryKey: ['minigridLocations'],
    queryFn: () => minigridAPI.getMinigridLocations(),
  });

  const locations = useMemo(
    () => locationsQuery.data || [],
    [locationsQuery.data],
  );

  useEffect(() => {
    if (
      initLocationNames &&
      initLocationNames.length > 0 &&
      locations.length > 0 &&
      !initLoaded &&
      value.length === 0
    ) {
      const wantedSet = new Set(
        initLocationNames.map((n) => n.trim().toLowerCase()),
      );
      const found = locations.filter((l) =>
        wantedSet.has(l.minigridName.trim().toLowerCase()),
      );
      if (found.length > 0) {
        onChange(found);
        setInitLoaded(true);
      }
    }
  }, [locations, initLocationNames, initLoaded, value.length]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = valueQuery.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((loc) =>
      loc.minigridName.toLowerCase().includes(q),
    );
  }, [locations, valueQuery]);

  const selectedNames = useMemo(
    () =>
      new Set(value.map((v) => v.minigridName.trim().toLowerCase())),
    [value],
  );

  const isSelected = (loc: MinigridLocationEnriched) =>
    selectedNames.has(loc.minigridName.trim().toLowerCase());

  const isLinkedToAnother = (loc: MinigridLocationEnriched) =>
    !!loc.linkedMinigrid &&
    loc.linkedMinigrid.minigrid_id !== (currentMinigridId || '');

  const formatItemName = (loc: MinigridLocationEnriched) => {
    const meterLabel = `${loc.meterCount} compteur${loc.meterCount > 1 ? 's' : ''}`;
    const linkedNote = isLinkedToAnother(loc)
      ? ` — déjà lié à « ${loc.linkedMinigrid!.minigrid_name} »`
      : '';
    const selectedMark = isSelected(loc) ? '✓ ' : '';
    return `${selectedMark}${loc.minigridName} (${meterLabel})${linkedNote}`;
  };

  const toggleSelection = (loc: MinigridLocationEnriched) => {
    const key = loc.minigridName.trim().toLowerCase();
    const exists = selectedNames.has(key);
    const next = exists
      ? value.filter(
          (v) => v.minigridName.trim().toLowerCase() !== key,
        )
      : [...value, loc];
    onChange(next);
    if (onUserSelect) onUserSelect(next, exists ? null : loc);
  };

  const removeChip = (loc: MinigridLocationEnriched) => {
    const key = loc.minigridName.trim().toLowerCase();
    const next = value.filter(
      (v) => v.minigridName.trim().toLowerCase() !== key,
    );
    onChange(next);
    if (onUserSelect) onUserSelect(next, null);
  };

  const onClickItem = (item: any) => {
    toggleSelection(item.item as MinigridLocationEnriched);
  };

  const convertToResultItem = (locs: MinigridLocationEnriched[]) =>
    locs.map((loc) => ({
      id: loc.minigridName,
      name: formatItemName(loc),
      item: loc,
    }));

  const summaryLabel =
    value.length === 0
      ? undefined
      : `${value.length} localisation${value.length > 1 ? 's' : ''} sélectionnée${value.length > 1 ? 's' : ''}`;

  return (
    <div>
      {value.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {value.map((loc) => (
            <span key={loc.minigridName} style={chipStyle}>
              {loc.minigridName}
              <span
                style={chipRemoveStyle}
                onClick={() => removeChip(loc)}
                title="Retirer"
              >
                ×
              </span>
            </span>
          ))}
        </div>
      )}
      <MicroModalSelectorSearch
        valueSelectedName={summaryLabel}
        placeholder="Sélectionner une ou plusieurs localisations de minigrid"
        result={convertToResultItem(filtered)}
        setQuery={setValueQuery}
        onClickItem={onClickItem}
        isLoading={locationsQuery.isLoading}
        valueQuery={valueQuery}
        isError={isError}
        emptyMessage="Aucune localisation trouvée"
        variant={variant}
        label={label}
      />
    </div>
  );
}
